import React, { useState, useEffect } from 'react';
import QRCode from 'react-qr-code';
import axios from '../../config/api';
import { API_URL } from '../../config/api';
import { MdVerified, MdSchedule, MdError, MdQrCode2, MdRefresh } from 'react-icons/md';

function VerificationSection({ profile }) {
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [verification, setVerification] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(false);
  const [showQR, setShowQR] = useState(false);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/verification/status`);
      setVerificationStatus(res.data.status);
      setVerification(res.data.verification);
    } catch {
      setVerificationStatus('unverified');
    } finally {
      setLoading(false);
    }
  };

  const requestVerification = async () => {
    setRequesting(true);
    try {
      const res = await axios.post(`${API_URL}/api/verification/request`);
      setVerification(res.data);
      setVerificationStatus('pending');
      setShowQR(true);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to request verification');
    } finally {
      setRequesting(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-gray-50 rounded-lg p-3 animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-1/3"></div>
      </div>
    );
  }

  if (verificationStatus === 'verified') {
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3" data-testid="verification-verified">
        <div className="flex items-center gap-2">
          <MdVerified className="text-xl text-blue-500" />
          <div>
            <p className="text-sm font-medium text-blue-800">Identity Verified</p>
            <p className="text-xs text-blue-600">Your profile has been verified and displays a verified badge.</p>
          </div>
        </div>
      </div>
    );
  }

  if (verificationStatus === 'submitted') {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="verification-submitted">
        <div className="flex items-center gap-2">
          <MdSchedule className="text-xl text-amber-500" />
          <div>
            <p className="text-sm font-medium text-amber-800">Verification Under Review</p>
            <p className="text-xs text-amber-600">Your selfie has been submitted. An admin will review it shortly.</p>
          </div>
        </div>
      </div>
    );
  }

  if (verificationStatus === 'rejected') {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-3" data-testid="verification-rejected">
        <div className="flex items-center gap-2 mb-2">
          <MdError className="text-xl text-red-500" />
          <div>
            <p className="text-sm font-medium text-red-800">Verification Rejected</p>
            <p className="text-xs text-red-600">
              {verification?.admin_notes || 'Your identity could not be verified. Please try again with a clear photo.'}
            </p>
          </div>
        </div>
        <button
          onClick={requestVerification}
          disabled={requesting}
          className="w-full mt-1 px-3 py-2 text-xs bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-1"
          data-testid="retry-verification-btn"
        >
          <MdRefresh className="text-sm" />
          {requesting ? 'Requesting...' : 'Try Again'}
        </button>
      </div>
    );
  }

  // Unverified or pending (QR generated but selfie not submitted yet)
  return (
    <div className="bg-gray-50 rounded-lg p-3" data-testid="verification-unverified">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Identity Verification</span>
      </div>

      {!showQR && verificationStatus !== 'pending' ? (
        <div>
          <p className="text-xs text-gray-600 mb-2">
            Verify your identity to earn a verified badge on your profile. Voters trust verified candidates more.
          </p>
          <button
            onClick={requestVerification}
            disabled={requesting || !profile.profile_picture_url}
            className="w-full px-3 py-2 text-xs bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-1"
            data-testid="request-verification-btn"
          >
            <MdQrCode2 className="text-sm" />
            {requesting ? 'Generating...' : 'Start Verification'}
          </button>
          {!profile.profile_picture_url && (
            <p className="text-xs text-amber-600 mt-1">Upload a profile picture first to verify your identity.</p>
          )}
        </div>
      ) : (
        <div>
          {(showQR || verificationStatus === 'pending') && verification && (
            <div className="text-center">
              <p className="text-xs text-gray-600 mb-3">
                Scan this QR code with your phone to take a verification selfie.
              </p>
              <div className="bg-white p-4 rounded-lg inline-block border border-gray-200 mb-3">
                <QRCode
                  value={verification.verification_url || `${window.location.origin}/verify/${verification.token}`}
                  size={160}
                  data-testid="verification-qr-code"
                />
              </div>
              <p className="text-xs text-gray-400">
                Or open on your phone:{' '}
                <a
                  href={verification.verification_url || `${window.location.origin}/verify/${verification.token}`}
                  className="text-primary hover:underline break-all"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="verification-link"
                >
                  Verify Now
                </a>
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default VerificationSection;
